<template>
  <div>
    <!-- Monthly/Yearly toggle — only when at least one tier offers yearly. -->
    <div v-if="hasYearly" class="billing-toggle" role="group" aria-label="Billing interval">
      <button
        type="button"
        class="toggle-option"
        :class="{ active: billing === 'month' }"
        @click="billing = 'month'"
      >{{ $t('pricing.billing_monthly') }}</button>
      <button
        type="button"
        class="toggle-option"
        :class="{ active: billing === 'year' }"
        @click="billing = 'year'"
      >
        {{ $t('pricing.billing_yearly') }}
        <span v-if="maxSavePct > 0" class="save-badge">−{{ maxSavePct }}%</span>
      </button>
    </div>

    <div class="plans-grid">
      <PricingCard
        v-for="(plan, index) in plans"
        :key="plan.name"
        :plan="plan"
        :is-featured="isFeatured(index)"
        :show-button="showButton"
        :loading="loading"
        @subscribe="$emit('subscribe', plan)"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
const props = defineProps({
  showButton: {
    type: Boolean,
    default: true
  },
  loading: {
    type: Boolean,
    default: false
  },
  /** 'month' | 'year' — lets /pricing?billing=year deep-link the yearly view. */
  initialBilling: {
    type: String,
    default: 'month'
  }
})

defineEmits(['subscribe'])

const { getPlans: getFallbackPlans } = usePricingTable()
const { tenant } = useTenantTheme()
const { t } = useI18n()

const round2 = (n: number) => Math.round(n * 100) / 100

// A stored keepRoyalties string wins; otherwise derive a LOCALIZED label
// from the structured share (the admin PUT stores null on purpose so this
// stays translatable).
const keepLabel = (p: any) => p.keepRoyalties
  || (typeof p.royaltySharePct === 'number'
    ? t('pricing.you_keep', { pct: round2(100 - p.royaltySharePct) })
    : '')

const billing = ref<'month' | 'year'>(props.initialBilling === 'year' ? 'year' : 'month')

// Source plans (DB when subscriptions are on, static fallback otherwise) —
// interval-agnostic.
const rawPlans = computed(() => {
  const dbPlans = (tenant.value?.subscriptionsEnabled && Array.isArray(tenant.value?.plans))
    ? tenant.value!.plans
    : []
  return dbPlans.length ? dbPlans : getFallbackPlans()
})

const hasYearly = computed(() => rawPlans.value.some((p: any) => Number(p.yearlyPrice) > 0))

const savePctFor = (p: any) => {
  const monthly = Number(p.price)
  const yearly = Number(p.yearlyPrice)
  if (!(monthly > 0) || !(yearly > 0)) return 0
  return Math.max(0, Math.round((1 - yearly / (monthly * 12)) * 100))
}
const maxSavePct = computed(() => Math.max(0, ...rawPlans.value.map(savePctFor)))

// Interval-resolved card models. In the yearly view a tier WITHOUT a
// yearly price keeps its monthly card (the Free tier, typically);
// promotional sales are monthly-only (the Stripe coupon terms are
// month-based and checkout only applies them to the monthly price).
const plans = computed(() => {
  return rawPlans.value.map((p: any) => {
    const yearlyActive = billing.value === 'year' && Number(p.yearlyPrice) > 0
    const saleActive = !yearlyActive
      && p.price > 0
      && !!p.sale?.enabled
      && p.sale.percentOff > 0
      && (!p.sale.availableUntil || new Date(p.sale.availableUntil) >= new Date())
    return {
      name: p.name,
      displayName: p.displayName || null,
      price: yearlyActive ? p.yearlyPrice : p.price,
      billingInterval: yearlyActive ? 'year' : 'month',
      monthlyEquiv: yearlyActive ? round2(p.yearlyPrice / 12) : null,
      savePct: yearlyActive ? savePctFor(p) : 0,
      keepRoyalties: keepLabel(p),
      features: Array.isArray(p.features) ? p.features : [],
      sale: saleActive ? {
        percentOff: p.sale.percentOff,
        durationMonths: p.sale.durationMonths,
        label: p.sale.label || null,
        salePrice: round2(p.price * (1 - p.sale.percentOff / 100))
      } : null
    }
  })
})

// Highlight the middle card when there are exactly three tiers.
const isFeatured = (index: number) => index === 1 && plans.value.length === 3
</script>

<style scoped>
/* Mobile-first. Ladder: 480 / 640 / 768 / 1024 / 1280 (variables.css). */
.billing-toggle {
  display: inline-flex;
  gap: 4px;
  padding: 4px;
  margin: 0 auto 28px;
  border: 1px solid var(--border);
  border-radius: 999px;
  background: var(--surface-color);
  position: relative;
  left: 50%;
  transform: translateX(-50%);
}

.toggle-option {
  border: none;
  background: transparent;
  color: var(--text-muted);
  font-size: 0.85rem;
  font-weight: 600;
  padding: 8px 18px;
  border-radius: 999px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.toggle-option.active {
  background: linear-gradient(135deg, rgb(var(--primary-channels) / 0.18), rgb(var(--secondary-channels) / 0.18));
  color: #fff;
}

.save-badge {
  font-size: 0.68rem;
  font-weight: 800;
  padding: 2px 7px;
  border-radius: 999px;
  background: rgb(var(--primary-channels) / 0.18);
  color: var(--primary);
}

.plans-grid {
  display: grid;
  grid-template-columns: 1fr;
  max-width: 420px;
  margin: 0 auto;
  gap: 24px;
  align-items: stretch;
}

@media (min-width: 768px) {
  .plans-grid {
    grid-template-columns: repeat(2, 1fr);
    max-width: none;
    margin: 0;
  }
}

@media (min-width: 1024px) {
  .plans-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}
</style>
